import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const fmtEur = (n) => {
    const v = n || 0;
    if (v >= 1e9) return `${(v / 1e9).toLocaleString("it-IT", { maximumFractionDigits: 2 })} mld`;
    return `${(v / 1e6).toLocaleString("it-IT", { maximumFractionDigits: 1 })} mln`;
};

function TeamRow({ team, rank, maxTotal }) {
    const [open, setOpen] = useState(false);
    const pct = maxTotal > 0 ? Math.max(4, Math.round((team.total / maxTotal) * 100)) : 0;
    const medal = rank === 1 ? "rgb(var(--tc-ffd700))" : rank === 2 ? "rgb(var(--tc-c0c0c0))" : rank === 3 ? "rgb(var(--tc-cd7f32))" : null;
    return (
        <div data-testid={`albo-premi-team-${team.team_id}`}>
            <button
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 hover:bg-t0e1014 text-left transition-colors"
                data-testid={`albo-premi-team-toggle-${team.team_id}`}
            >
                <span
                    className="op-mono text-xs font-bold w-6 shrink-0 text-center"
                    style={{ color: medal || "rgb(var(--tc-5c6370))" }}
                >
                    {rank}
                </span>
                <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-3">
                        <span className="op-mono text-sm text-tffffff truncate">{team.team_name}</span>
                        <span className="op-mono text-sm text-t7ce38b shrink-0 font-bold">{fmtEur(team.total)}</span>
                    </div>
                    <div className="mt-1.5 h-1.5 bg-t16181c overflow-hidden rounded-full">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg,rgb(var(--tc-2bb6ff)),rgb(var(--tc-7ce38b)))" }} />
                    </div>
                </div>
                <span className="op-mono text-[10px] text-t5c6370 shrink-0 w-12 text-right">{open ? "chiudi" : `${team.lines.length} voci`}</span>
            </button>
            {open && (
                <div className="px-3 sm:px-4 pb-3 pt-1 bg-t0a0b0e" data-testid={`albo-premi-team-detail-${team.team_id}`}>
                    {team.by_category?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                            {team.by_category.map((c) => (
                                <span key={c.key} className="op-mono text-[10px] px-2 py-0.5 border border-t22252a text-t8f95a3 rounded-full">
                                    {c.label}: <span className="text-t7ce38b">{fmtEur(c.total)}</span>
                                </span>
                            ))}
                        </div>
                    )}
                    <div className="divide-y divide-t16181c">
                        {team.lines.map((ln, i) => (
                            <div key={i} className="flex justify-between op-mono text-[11px] text-tc9ccd3 py-1">
                                <span className="truncate pr-3">{ln.voce}</span>
                                <span className="text-t7ce38b shrink-0">+{fmtEur(ln.amount)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function SeasonCard({ season }) {
    const maxTotal = season.teams[0]?.total || 0;
    return (
        <div className="op-card overflow-hidden mb-8" style={{ borderTop: "3px solid rgb(var(--tc-7ce38b))" }} data-testid={`albo-premi-season-${season.season_label}`}>
            <div className="px-5 py-4 border-b border-t22252a bg-t0e1014 flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <div className="op-label text-[10px] text-t7ce38b">// STAGIONE</div>
                    <div className="op-heading text-tffffff text-xl tracking-wide">{season.season_label}</div>
                </div>
                <div className="text-right">
                    <div className="op-label text-[10px] text-t8f95a3">Totale distribuito</div>
                    <div className="op-heading text-2xl text-t7ce38b" data-testid={`albo-premi-season-total-${season.season_label}`}>{fmtEur(season.total)}</div>
                    <div className="op-mono text-[10px] text-t5c6370">{season.teams_count} squadre</div>
                </div>
            </div>

            {season.categories?.length > 0 && (
                <div className="px-4 py-3 border-b border-t16181c bg-t0a0b0e flex flex-wrap gap-2">
                    {season.categories.map((c) => (
                        <span key={c.key} className="op-mono text-[10px] px-2.5 py-1 border border-t22252a text-t8f95a3">
                            {c.label} · <span className="text-tffffff">{fmtEur(c.total)}</span>
                        </span>
                    ))}
                </div>
            )}

            <div className="divide-y divide-t16181c">
                {season.teams.map((t, i) => (
                    <TeamRow key={t.team_id} team={t} rank={i + 1} maxTotal={maxTotal} />
                ))}
            </div>
        </div>
    );
}

export default function AlboPremi() {
    const [seasons, setSeasons] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/prizes/albo");
                setSeasons(data.seasons || []);
            } catch (e) {
                console.error(e);
            } finally { setLoading(false); }
        })();
    }, []);

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="albo-premi-page">
            <div className="op-label" style={{ color: "rgb(var(--tc-7ce38b))" }}>// ECONOMIA DEL TORNEO</div>
            <h1 className="op-heading text-5xl sm:text-6xl mt-3">
                <span className="op-gradient-text">ALBO PREMI</span>
            </h1>
            <p className="text-t8f95a3 text-sm mt-3 max-w-3xl">
                Quanto ha incassato ogni squadra dai premi di fine stagione (cassa OP). Clicca una squadra per il dettaglio voce per voce.
            </p>

            {loading ? (
                <div className="op-label text-t8f95a3 animate-pulse mt-10">// CARICAMENTO…</div>
            ) : seasons.length === 0 ? (
                <div className="op-card p-8 mt-8 text-center op-mono text-sm text-t5c6370" data-testid="albo-premi-empty">
                    Nessun premio ancora distribuito.
                </div>
            ) : (
                <div className="mt-8">
                    {seasons.map((s) => (
                        <SeasonCard key={s.season_label} season={s} />
                    ))}
                </div>
            )}
        </div>
    );
}
